import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ID } from "appwrite";
import {
  tablesDB, account, storage,
  DB_ID, ORDERS_TABLE, BUCKET_ID,
} from "../../appwrite";
import {
  ArrowLeft, CheckCircle, Clock, Truck, XCircle,
  Package, ImagePlus, X, User, Phone,
} from "lucide-react";
import { toast } from "react-toastify";
import { useTitle } from "../../hooks/useTitle";
import LoadingScreen from "../../LoadingScreen";
import "./SellerOrDet.css";

const fmtPrice = (v) =>
  "₦" + parseFloat(v || 0).toLocaleString("en-NG", { minimumFractionDigits: 0 });

const fmtDate = (v) => {
  if (!v) return "-";
  try {
    return new Date(v).toLocaleString("en-NG", {
      day: "numeric", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return "-"; }
};

const parseInfoArray = (arr) => {
  if (!Array.isArray(arr)) return {};
  const r = {};
  arr.forEach((item) => {
    if (typeof item === "string") {
      try { Object.assign(r, JSON.parse(item)); }
      catch { const i = item.indexOf(":"); if (i > -1) r[item.slice(0, i).trim()] = item.slice(i + 1).trim(); }
    }
  });
  return r;
};

const STATUSES = [
  { value: "pending",          label: "Pending",          icon: Clock,        cls: "sod-status--pending" },
  { value: "processing",       label: "Processing",       icon: Package,      cls: "sod-status--processing" },
  { value: "out-for-delivery", label: "Out for Delivery", icon: Truck,        cls: "sod-status--shipping" },
  { value: "delivered",        label: "Delivered",        icon: CheckCircle,  cls: "sod-status--delivered" },
  { value: "cancelled",        label: "Cancelled",        icon: XCircle,      cls: "sod-status--cancelled" },
];

export default function SellerOrDet() {
  useTitle("Order Details - TrendBay Seller");
  const { orderId } = useParams();
  const navigate = useNavigate();
  const fileRef = useRef();

  const [order, setOrder]               = useState(null);
  const [myItems, setMyItems]           = useState([]);
  const [loading, setLoading]           = useState(true);
  const [updating, setUpdating]         = useState(false);
  const [userId, setUserId]             = useState(null);
  const [dispatchProof, setDispatchProof] = useState([]);
  const [newFiles, setNewFiles]         = useState([]);
  const [uploading, setUploading]       = useState(false);
  const [rider, setRider]               = useState({ name: "", phone: "" });

  useEffect(() => {
    const meta = document.querySelector("meta[name='theme-color']");
    const orig = meta?.getAttribute("content");
    if (meta) meta.setAttribute("content", "#080F0D");
    return () => { if (meta) meta.setAttribute("content", orig); };
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const user = await account.get();
        setUserId(user.$id);

        const row = await tablesDB.getRow({
          databaseId: DB_ID, tableId: ORDERS_TABLE, rowId: orderId,
        });
        setOrder({ ...row, id: row.$id });
        setRider({ name: row.riderName || "", phone: row.riderPhone || "" });

        const proof = row.dispatchProof || [];
        setDispatchProof(Array.isArray(proof) ? proof : []);

        const parsedCart = (row.cart || []).map((item) => {
          if (typeof item === "string") { try { return JSON.parse(item); } catch { return {}; } }
          return item;
        });
        setMyItems(parsedCart.filter((item) => item.sellerId === user.$id));
      } catch (e) {
        console.error(e);
        toast.error("Could not load order");
      } finally {
        setLoading(false);
      }
    })();
  }, [orderId]);

  const handleStatusChange = async (newStatus) => {
    if (newStatus === "out-for-delivery" && dispatchProof.length === 0 && newFiles.length === 0) {
      toast.warn("Save rider info & dispatch proof before marking as Out for Delivery.");
      return;
    }
    if (updating) return;
    setUpdating(true);
    try {
      await tablesDB.updateRow({
        databaseId: DB_ID, tableId: ORDERS_TABLE, rowId: orderId,
        data: { fulfillmentStatus: newStatus },
      });
      setOrder((prev) => ({ ...prev, fulfillmentStatus: newStatus }));
      toast.success("Status updated");

      if (newStatus === "delivered") {
        const buyer = parseInfoArray(order.shippingInfo);
        if (buyer.email) {
          fetch("https://email-api.trendbayds.com/send", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              type: "order_delivered",
              payload: {
                buyerEmail: buyer.email,
                buyerName: buyer.fullName || "Customer",
                orderNumber: order.orderNumber,
                transactionRef: order.paymentReference?.reference || order.paymentReference,
                items: myItems.map((i) => ({ title: i.title || i.productName, quantity: i.quantity || 1 })),
              },
            }),
          }).catch(() => {});
        }
      }
    } catch (e) {
      console.error(e);
      toast.error("Failed to update status");
    } finally {
      setUpdating(false);
    }
  };

  const addFiles = async (files) => {
    const entries = await Promise.all(
      Array.from(files).map(async (file) => {
        const preview = await new Promise((res) => {
          const r = new FileReader();
          r.onloadend = () => res(r.result);
          r.readAsDataURL(file);
        });
        return { file, preview };
      })
    );
    setNewFiles((prev) => [...prev, ...entries]);
  };

  const removeNewFile = (idx) => setNewFiles((prev) => prev.filter((_, i) => i !== idx));

  const handleDispatchSave = async () => {
    if (!rider.name.trim() || !rider.phone.trim()) {
      toast.warn("Enter rider name and phone number.");
      return;
    }
    setUploading(true);
    try {
      const { Permission, Role } = await import("appwrite");
      const urls = await Promise.all(
        newFiles.map(async ({ file }) => {
          const fileId = ID.unique();
          await storage.createFile(BUCKET_ID, fileId, file, [Permission.read(Role.any())]);
          return `https://storage.trendbayds.com/f/${fileId}`;
        })
      );
      const allProof = [...dispatchProof, ...urls];
      await tablesDB.updateRow({
        databaseId: DB_ID, tableId: ORDERS_TABLE, rowId: orderId,
        data: {
          dispatchProof: allProof,
          fulfillmentStatus: "out-for-delivery",
          riderName: rider.name.trim(),
          riderPhone: rider.phone.trim(),
        },
      });
      setDispatchProof(allProof);
      setNewFiles([]);
      setOrder((prev) => ({ ...prev, fulfillmentStatus: "out-for-delivery", riderName: rider.name.trim(), riderPhone: rider.phone.trim() }));
      toast.success("Dispatch proof saved · Status set to Out for Delivery");

      // Fire-and-forget dispatch notification email
      const WORKER = "https://email-api.trendbayds.com";
      if (WORKER) {
        const buyer = parseInfoArray(order.shippingInfo);
        if (buyer.email) {
          fetch(`${WORKER}/send`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              type: "dispatch_notification",
              payload: {
                buyerEmail: buyer.email,
                buyerName: buyer.fullName || "Customer",
                orderNumber: order.orderNumber,
                riderName: rider.name.trim(),
                riderPhone: rider.phone.trim(),
                items: myItems.map((i) => ({ title: i.title || i.productName, quantity: i.quantity || 1 })),
              },
            }),
          }).catch(() => {});
        }
      }
    } catch (e) {
      console.error(e);
      toast.error("Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  if (loading) return <LoadingScreen />;
  if (!order) return null;

  const shippingInfo = parseInfoArray(order.shippingInfo);
  const billingInfo  = parseInfoArray(order.billingInfo);
  const paid = (order.paymentStatus || "").toLowerCase() === "paid";
  const currentStatus = order.fulfillmentStatus || "pending";
  const canUpload = !["delivered", "cancelled"].includes(currentStatus);

  return (
    <div className="sod-page">

      {/* Header */}
      <div className="sod-header">
        <button className="sod-back" onClick={() => navigate("/s/orders")}>
          <ArrowLeft size={15} /> Back to Orders
        </button>
        <div className="sod-header-right">
          <h1 className="sod-title">Order #{order.orderNumber || "-"}</h1>
          <div className="sod-meta-row">
            <span className={`sod-badge ${paid ? "sod-badge--paid" : "sod-badge--unpaid"}`}>
              {paid ? "Paid" : order.paymentStatus || "Unpaid"}
            </span>
            <span className={`sod-badge sod-badge--${(currentStatus === "out-for-delivery" ? "shipping" : currentStatus)}`}>
              {STATUSES.find((s) => s.value === currentStatus)?.label || currentStatus}
            </span>
            <span className="sod-date">{fmtDate(order.$createdAt)}</span>
          </div>
        </div>
      </div>

      <div className="sod-body">

        {/* LEFT */}
        <div className="sod-main">

          {/* Items */}
          <div className="sod-card">
            <h3 className="sod-card-title">Your Items in This Order</h3>
            {myItems.length === 0 ? (
              <p className="sod-empty">No items found for your account in this order.</p>
            ) : (
              myItems.map((item, i) => (
                <div className="sod-item" key={i}>
                  {(item.image || item.productImage) && (
                    <img
                      className="sod-item-img"
                      src={item.image || item.productImage}
                      alt={item.title || item.productName}
                    />
                  )}
                  <div className="sod-item-info">
                    <p className="sod-item-name">{item.title || item.productName || "-"}</p>
                    <p className="sod-item-meta">Qty: {item.quantity || 1}</p>
                  </div>
                  <p className="sod-item-price">{fmtPrice(item.price)}</p>
                </div>
              ))
            )}
            <div className="sod-summary">
              <div className="sod-summary-row">
                <span>Your items total</span>
                <strong>
                  {fmtPrice(myItems.reduce((s, item) =>
                    s + (parseFloat(item.price) || 0) * (item.quantity || 1), 0))}
                </strong>
              </div>
            </div>
          </div>

          {/* Customer */}
          <div className="sod-card">
            <h3 className="sod-card-title">Customer</h3>
            <div className="sod-info-grid">
              <div>
                <p className="sod-info-label">Name</p>
                <p className="sod-info-val">{shippingInfo.fullName || "-"}</p>
              </div>
              <div>
                <p className="sod-info-label">Phone</p>
                <p className="sod-info-val">{shippingInfo.phoneNumber || "-"}</p>
              </div>
            </div>
          </div>

          {/* Addresses */}
          <div className="sod-card sod-addr-row">
            <div>
              <h3 className="sod-card-title">Shipping Address</h3>
              <p className="sod-addr">{shippingInfo.address || "-"}</p>
              <p className="sod-addr">
                {[shippingInfo.city, shippingInfo.state].filter(Boolean).join(", ")}
              </p>
              <p className="sod-addr">{shippingInfo.country || ""}</p>
            </div>
            <div>
              <h3 className="sod-card-title">Billing</h3>
              <p className="sod-addr">{billingInfo.fullName || "-"}</p>
              <p className="sod-addr">{billingInfo.address || "-"}</p>
              <p className="sod-addr">
                {[billingInfo.city, billingInfo.state].filter(Boolean).join(", ")}
              </p>
            </div>
          </div>

        </div>

        {/* RIGHT */}
        <div className="sod-sidebar">

          {/* Fulfillment */}
          <div className="sod-card">
            <h3 className="sod-card-title">Fulfillment Status</h3>
            <div className="sod-status-list">
              {STATUSES.map(({ value, label, icon: Icon, cls }) => (
                <button
                  key={value}
                  className={`sod-status-btn ${cls} ${currentStatus === value ? "sod-status-btn--active" : ""}`}
                  onClick={() => handleStatusChange(value)}
                  disabled={updating}
                >
                  <Icon size={14} />
                  {label}
                  {currentStatus === value && <span className="sod-status-check">✓</span>}
                </button>
              ))}
            </div>
          </div>

          {/* Dispatch Proof */}
          <div className="sod-card">
            <h3 className="sod-card-title">Dispatch Proof</h3>
            <p className="sod-dispatch-hint">
              {dispatchProof.length > 0
                ? "Photos sent to the buyer confirming dispatch."
                : "Required before setting status to Out for Delivery."}
            </p>

            {/* Existing saved proof */}
            {dispatchProof.length > 0 && (
              <div className="sod-proof-grid">
                {dispatchProof.map((url, i) => (
                  <img key={i} src={url} alt={`Dispatch proof ${i + 1}`} className="sod-proof-img" />
                ))}
              </div>
            )}

            {/* Upload new */}
            {canUpload && (
              <>
                <div className="sod-rider-fields">
                  <div className="sod-rider-field">
                    <label className="sod-rider-label"><User size={12} /> Rider Name</label>
                    <input
                      className="sod-rider-input"
                      type="text"
                      placeholder="e.g. Emeka Okafor"
                      value={rider.name}
                      onChange={(e) => setRider((r) => ({ ...r, name: e.target.value }))}
                    />
                  </div>
                  <div className="sod-rider-field">
                    <label className="sod-rider-label"><Phone size={12} /> Rider Phone</label>
                    <input
                      className="sod-rider-input"
                      type="tel"
                      placeholder="e.g. 08012345678"
                      value={rider.phone}
                      onChange={(e) => setRider((r) => ({ ...r, phone: e.target.value }))}
                    />
                  </div>
                </div>

                <div
                  className="sod-drop"
                  onClick={() => fileRef.current.click()}
                >
                  <ImagePlus size={22} color="#00A855" />
                  <p>Tap to add photos</p>
                </div>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  multiple
                  style={{ display: "none" }}
                  onChange={(e) => addFiles(e.target.files)}
                />

                {newFiles.length > 0 && (
                  <div className="sod-proof-grid sod-proof-grid--new">
                    {newFiles.map((f, i) => (
                      <div key={i} className="sod-proof-thumb">
                        <img src={f.preview} alt="" />
                        <button className="sod-proof-remove" onClick={() => removeNewFile(i)}>
                          <X size={11} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <button
                  className="sod-dispatch-btn"
                  onClick={handleDispatchSave}
                  disabled={uploading}
                >
                  <Truck size={14} />
                  {uploading ? "Uploading…" : "Save & Mark Out for Delivery"}
                </button>
              </>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}
